import { container } from '../di/container';
import { HealthBar } from './health-bar';
import { DEBUG } from '../debug';
import { SoundManager } from '../audio/sound-manager';

export class HUD {
	private healthBar: HealthBar;
	private showGameOverScreen: boolean = false;
	private showRestartPrompt: boolean = false;
	private isColliding: boolean = false;

	// Settings menu
	private showSettingsMenu: boolean = false;
	private settingsButtonX: number = 0;
	private settingsButtonY: number = 10;
	private settingsButtonSize: number = 30;

	// LocalStorage keys
	private static readonly STORAGE_KEY_DEBUG = 'game_debug_collision';
	private static readonly STORAGE_KEY_VOLUME = 'game_volume';

	constructor(maxHealth: number = 9) {
		this.healthBar = new HealthBar(25, 556, maxHealth);
		this.loadSettings();
		this.initMouseListener();
	}

	private loadSettings(): void {
		// Load debug setting
		const debugValue = localStorage.getItem(HUD.STORAGE_KEY_DEBUG);
		if (debugValue !== null) {
			DEBUG.COLLISION = debugValue === 'true';
		}

		// Load volume setting
		const volumeValue = localStorage.getItem(HUD.STORAGE_KEY_VOLUME);
		if (volumeValue !== null) {
			const soundManager = container.resolve(SoundManager);
			soundManager.volume = parseFloat(volumeValue);
		}
	}

	private saveDebugSetting(): void {
		localStorage.setItem(HUD.STORAGE_KEY_DEBUG, DEBUG.COLLISION.toString());
	}

	private saveVolumeSetting(volume: number): void {
		localStorage.setItem(HUD.STORAGE_KEY_VOLUME, volume.toString());
	}

	private initMouseListener(): void {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const p5Instance = window as any;
		p5Instance.mousePressed = () => {
			this.handleClick(p5Instance.mouseX, p5Instance.mouseY);
		};
		// Slider için drag desteği
		p5Instance.mouseDragged = () => {
			if (this.showSettingsMenu) {
				this.handleSliderDrag(p5Instance.mouseX, p5Instance.mouseY);
			}
		};
	}

	private handleSliderDrag(mx: number, my: number): void {
		const menuX = width - 220;
		const menuY = 50;
		const volumeY = menuY + 65;
		const sliderY = volumeY + 15;

		// Volume slider alanında mı?
		if (mx >= menuX + 5 && mx <= menuX + 205 && my >= sliderY - 15 && my <= sliderY + 25) {
			const sliderX = menuX + 10;
			const sliderWidth = 190;
			const newVolume = (mx - sliderX) / sliderWidth;
			const soundManager = container.resolve(SoundManager);
			soundManager.volume = Math.max(0, Math.min(1, newVolume));
			this.saveVolumeSetting(soundManager.volume);
		}
	}

	private handleClick(mx: number, my: number): void {
		// Settings button click
		const btnX = width - this.settingsButtonSize - 10;
		if (
			mx >= btnX &&
			mx <= btnX + this.settingsButtonSize &&
			my >= this.settingsButtonY &&
			my <= this.settingsButtonY + this.settingsButtonSize
		) {
			this.showSettingsMenu = !this.showSettingsMenu;
			return;
		}

		// Settings menu interactions
		if (this.showSettingsMenu) {
			const menuX = width - 220;
			const menuY = 50;

			// Debug toggle (checkbox area) - Y: menuY + 30, height: 20
			const debugY = menuY + 25;
			if (mx >= menuX + 5 && mx <= menuX + 205 && my >= debugY && my <= debugY + 30) {
				DEBUG.COLLISION = !DEBUG.COLLISION;
				this.saveDebugSetting();
				return;
			}

			// Volume slider - daha geniş alan
			const volumeY = menuY + 65;
			const sliderY = volumeY + 15;
			if (mx >= menuX + 5 && mx <= menuX + 205 && my >= sliderY - 10 && my <= sliderY + 20) {
				const sliderX = menuX + 10;
				const sliderWidth = 190;
				const newVolume = (mx - sliderX) / sliderWidth;
				const soundManager = container.resolve(SoundManager);
				soundManager.volume = Math.max(0, Math.min(1, newVolume));
				this.saveVolumeSetting(soundManager.volume);
				return;
			}

			// Close button
			if (mx >= menuX + 175 && mx <= menuX + 210 && my >= menuY && my <= menuY + 25) {
				this.showSettingsMenu = false;
				return;
			}
		}
	}

	// Callbacks to be set by game
	onRestartRequested?: () => void;

	// State updates
	setHealth(health: number): void {
		this.healthBar.currentHealth = health;
	}

	setGameOver(isGameOver: boolean): void {
		this.showGameOverScreen = isGameOver;
	}

	setRestartPrompt(show: boolean): void {
		this.showRestartPrompt = show;
	}

	setColliding(colliding: boolean): void {
		this.isColliding = colliding;
	}

	get currentHealth(): number {
		return this.healthBar.currentHealth;
	}

	draw(): void {
		// Health bar (bottom-left)
		this.healthBar.draw();

		// FPS counter (top-left)
		this.drawFPS();

		// Settings button (top-right)
		this.drawSettingsButton();

		// Settings menu
		if (this.showSettingsMenu) {
			this.drawSettingsMenu();
		}

		// Collision debug indicator
		if (DEBUG.COLLISION && this.isColliding) {
			this.drawCollisionIndicator();
		}

		// Game Over / Restart overlay
		if (this.showGameOverScreen) {
			this.drawGameOver();
		} else if (this.showRestartPrompt) {
			this.drawRestartPrompt();
		}
	}

	private drawSettingsButton(): void {
		const btnX = width - this.settingsButtonSize - 10;
		this.settingsButtonX = btnX;

		push();
		// Button background
		noStroke();
		fill(this.showSettingsMenu ? 80 : 50, 50, 50, 200);
		rect(btnX, this.settingsButtonY, this.settingsButtonSize, this.settingsButtonSize, 5);

		// Gear icon (simple)
		fill(255);
		translate(
			btnX + this.settingsButtonSize / 2,
			this.settingsButtonY + this.settingsButtonSize / 2
		);

		// Center circle
		ellipse(0, 0, 8, 8);

		// Gear teeth
		for (let i = 0; i < 6; i++) {
			push();
			rotate((i * PI) / 3);
			rect(-2, -12, 4, 6);
			pop();
		}
		pop();
	}

	private drawSettingsMenu(): void {
		const menuX = width - 220;
		const menuY = 50;
		const menuWidth = 210;
		const menuHeight = 120;

		push();
		// Menu background
		noStroke();
		fill(30, 30, 30, 230);
		rect(menuX, menuY, menuWidth, menuHeight, 8);

		// Title
		fill(255);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).LEFT, (window as any).TOP);
		textSize(14);
		text('Settings', menuX + 10, menuY + 5);

		// Close button (X)
		fill(150);
		textSize(16);
		text('✕', menuX + 185, menuY + 3);

		// Debug toggle
		const debugY = menuY + 30;
		fill(DEBUG.COLLISION ? 100 : 60, DEBUG.COLLISION ? 180 : 60, DEBUG.COLLISION ? 100 : 60);
		rect(menuX + 10, debugY, 20, 20, 3);
		if (DEBUG.COLLISION) {
			fill(255);
			textSize(14);
			text('✓', menuX + 14, debugY + 3);
		}
		fill(255);
		textSize(12);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).LEFT, (window as any).CENTER);
		text('Debug Collision', menuX + 40, debugY + 10);

		// Volume slider
		const volumeY = menuY + 65;
		fill(255);
		textSize(12);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).LEFT, (window as any).TOP);
		text('Volume', menuX + 10, volumeY);

		const sliderY = volumeY + 18;
		const sliderWidth = 190;
		const soundManager = container.resolve(SoundManager);
		const volume = soundManager.volume;

		// Slider track
		fill(60);
		rect(menuX + 10, sliderY, sliderWidth, 8, 4);

		// Slider fill
		fill(100, 180, 100);
		rect(menuX + 10, sliderY, sliderWidth * volume, 8, 4);

		// Slider handle
		fill(255);
		const handleX = menuX + 10 + sliderWidth * volume;
		ellipse(handleX, sliderY + 4, 14, 14);

		// Volume percentage
		fill(150);
		textSize(10);
		text(`${Math.round(volume * 100)}%`, menuX + 160, volumeY);

		pop();
	}

	private drawFPS(): void {
		push();
		noStroke();
		fill(0, 0, 0, 150);
		rect(10, 10, 100, 30);
		fill(255);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).LEFT, (window as any).CENTER);
		textSize(16);
		text(`FPS: ${Math.round(frameRate())}`, 15, 25);
		pop();
	}

	private drawCollisionIndicator(): void {
		push();
		noStroke();
		fill(0, 0, 0, 150);
		rect(10, 45, 120, 25);
		fill(255, 0, 0);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).LEFT, (window as any).CENTER);
		textSize(16);
		text('COLLISION!', 15, 57);
		pop();
	}

	private drawGameOver(): void {
		push();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).CENTER, (window as any).CENTER);
		fill(0, 0, 0, 200);
		rect(width / 2 - 150, height / 2 - 40, 300, 80);
		fill(255, 0, 0);
		textSize(48);
		text('GAME OVER', width / 2, height / 2);
		pop();
	}

	private drawRestartPrompt(): void {
		push();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		textAlign((window as any).CENTER, (window as any).CENTER);
		fill(0, 0, 0, 180);
		rect(width / 2 - 120, height / 2 - 30, 240, 60);
		fill(255);
		textSize(20);
		text('Press ESC to restart', width / 2, height / 2);
		pop();
	}

	reset(): void {
		this.showGameOverScreen = false;
		this.showRestartPrompt = false;
		this.isColliding = false;
	}
}
